import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DeleteConfirmModal from '@/components/DeleteConfirmModal'
import { ArrowLeft, Save, Trash2, X, Shield, AlertTriangle } from 'lucide-react'

interface License {
  id: number
  agent_id: number
  agent_name: string
  state_code: string
  license_number: string
  primary_residence_state: boolean
  status: string
  issued_at: string
  expires_at: string
  days_until_expiry: number | null
  expiring_soon: boolean
}

interface Props extends PageProps {
  license: License
  is_admin: boolean
}

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
]

export default function LicenseShow({ license, is_admin }: Props) {
  const { auth } = usePage<PageProps>().props
  const canDelete = auth.user?.role === 'owner' || auth.user?.role === 'administrator'
  
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showPrimaryModal, setShowPrimaryModal] = useState(false)
  const [form, setForm] = useState({
    state_code: license.state_code,
    license_number: license.license_number,
    primary_residence_state: license.primary_residence_state,
    status: license.status,
    issued_at: license.issued_at,
    expires_at: license.expires_at
  })

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    expired: 'bg-red-100 text-red-700',
    revoked: 'bg-gray-100 text-gray-700'
  }

  const handlePrimaryChange = (checked: boolean) => {
    if (checked && !license.primary_residence_state) {
      setShowPrimaryModal(true)
    } else {
      setForm({ ...form, primary_residence_state: checked })
    }
  }

  const handleConfirmPrimary = () => {
    setShowPrimaryModal(false)
    const updatedForm = { ...form, primary_residence_state: true }
    setForm(updatedForm)
    router.patch(`/licenses/${license.id}`, {
      agent_license: updatedForm
    }, {
      onSuccess: () => setIsEditing(false)
    })
  }

  const handleSave = () => {
    if (form.primary_residence_state && !license.primary_residence_state) {
      setShowPrimaryModal(true)
      return
    }
    router.patch(`/licenses/${license.id}`, { agent_license: form }, {
      onSuccess: () => setIsEditing(false)
    })
  }

  const stateName = US_STATES.find(s => s.code === license.state_code)?.name || license.state_code

  return (
    <AppLayout>
      <Head title={`License: ${license.state_code} - ${license.license_number}`} />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/licenses" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">License Details</h1>
              <p className="text-chestnut-charcoal/70 mt-1">{stateName} - {license.license_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button onClick={() => setIsEditing(false)} className="btn-secondary flex items-center gap-2">
                  <X className="h-4 w-4" />
                  Cancel
                </button>
                <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  Save
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setIsEditing(true)} className="btn-secondary">
                  Edit
                </button>
                {canDelete && (
                  <button onClick={() => setShowDeleteModal(true)} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg">
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {license.expiring_soon && license.days_until_expiry !== null && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <div>
              <p className="font-medium text-orange-800">License Expiring Soon</p>
              <p className="text-sm text-orange-700">This license expires in {license.days_until_expiry} days. Please renew to maintain compliance.</p>
            </div>
          </div>
        )}

        <div className="card p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-chestnut-charcoal/10">
            <div className="p-3 bg-chestnut-forest/10 rounded-lg">
              <Shield className="h-6 w-6 text-chestnut-forest" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-chestnut-charcoal">License Information</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[license.status]}`}>
                  {license.status}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  license.primary_residence_state
                    ? 'bg-chestnut-forest/10 text-chestnut-forest'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {license.primary_residence_state ? 'Primary Residence State' : 'Non-Primary Residence State'}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {is_admin && (
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Agent</label>
                <p className="text-chestnut-charcoal font-medium">
                  <Link href={`/agents/${license.agent_id}`} className="text-chestnut-forest hover:underline">
                    {license.agent_name}
                  </Link>
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">State</label>
              {isEditing ? (
                <select
                  value={form.state_code}
                  onChange={(e) => setForm({ ...form, state_code: e.target.value })}
                  className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                >
                  {US_STATES.map((state) => (
                    <option key={state.code} value={state.code}>{state.name} ({state.code})</option>
                  ))}
                </select>
              ) : (
                <p className="text-chestnut-charcoal">{stateName} ({license.state_code})</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">License Number</label>
              {isEditing ? (
                <input
                  type="text"
                  value={form.license_number}
                  onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                  className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                />
              ) : (
                <p className="text-chestnut-charcoal font-mono">{license.license_number}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Status</label>
              {isEditing ? (
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                >
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="expired">Expired</option>
                  <option value="revoked">Revoked</option>
                </select>
              ) : (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[license.status]}`}>
                  {license.status}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Primary Residence State</label>
              {isEditing ? (
                <label className="flex items-center gap-2 cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    checked={form.primary_residence_state}
                    onChange={(e) => handlePrimaryChange(e.target.checked)}
                    className="w-4 h-4 text-chestnut-forest border-chestnut-charcoal/20 rounded focus:ring-chestnut-forest"
                  />
                  <span className="text-sm text-chestnut-charcoal">This is my primary residence state</span>
                </label>
              ) : (
                <p className="text-chestnut-charcoal">
                  {license.primary_residence_state ? 'Yes' : 'No'}
                </p>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-chestnut-charcoal/10">
            <h3 className="font-medium text-chestnut-charcoal mb-4">Validity Period</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Issued Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={form.issued_at}
                    onChange={(e) => setForm({ ...form, issued_at: e.target.value })}
                    className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                  />
                ) : (
                  <p className="text-chestnut-charcoal">{license.issued_at ? new Date(license.issued_at).toLocaleDateString() : '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Expiration Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                  />
                ) : (
                  <div>
                    <p className="text-chestnut-charcoal">{license.expires_at ? new Date(license.expires_at).toLocaleDateString() : '-'}</p>
                    {license.expiring_soon && license.days_until_expiry !== null && (
                      <p className="text-sm text-orange-600 font-medium mt-1">{license.days_until_expiry} days remaining</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete License"
        message="Are you sure you want to delete this license record? This action cannot be undone."
        deleteUrl={`/licenses/${license.id}`}
        itemName={`${stateName} License #${license.license_number}`}
        redirectUrl="/licenses"
      />

      {showPrimaryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowPrimaryModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-chestnut-charcoal mb-3">Change Primary Residence State</h3>
            <p className="text-sm text-chestnut-charcoal/70 mb-6">
              Are you sure that you want to set this license as your Primary Residence State and remove it from any other licenses you have recorded?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPrimaryModal(false)
                  setForm({ ...form, primary_residence_state: license.primary_residence_state })
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPrimary}
                className="btn-primary"
              >
                Yes, Set as Primary
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
